/**
 * FansPage - v2 drilldown. Live data only.
 *
 * Per CEO directive 2026-04-28: no mock values. Sections that the schema
 * cannot support (fan acquisition channel, cost-per-acquired) are explicitly
 * shown as "Not available — <reason>" cards rather than fabricated numbers.
 *
 * PII: top-spender names are masked to `fan_<6-hex>` server-side.
 */
import React from "react";
import { tokensV2 as T } from "./tokens.v2";
import { Section } from "./Section.v2";
import { Tile } from "./Tile.v2";
import { AreaChart } from "./AreaChart.v2";
import { Leaderboard } from "./Leaderboard.v2";
import { Donut } from "./Donut.v2";
import { PageGuide } from "./PageGuide.v2";
import { useFansData } from "./useFansData.v2";
import { rangeWordFor, capRangeWord } from "./rangeWord.v2";
import { LoadingShell as PageLoadingShell, EmptyError } from "./PageState.v2";

function formatINR(n) {
  if (n == null) return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  if (num >= 1e7) return "Rs " + (num / 1e7).toFixed(2) + "Cr";
  if (num >= 1e5) return "Rs " + (num / 1e5).toFixed(2) + "L";
  if (num >= 1e3) return "Rs " + (num / 1e3).toFixed(1) + "K";
  return "Rs " + Math.round(num);
}
function formatNum(n) {
  if (n == null) return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString("en-IN");
}

function UnavailableCard({ title, reason }) {
  return (
    <div style={{
      background: T.surface.card,
      border: `1px dashed ${T.border.default}`,
      borderRadius: 12,
      padding: 16,
      display: "flex", flexDirection: "column", gap: 6,
      minHeight: 120,
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.text.muted }}>
        Not available
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: T.text.primary }}>{title}</div>
      <div style={{ fontSize: 12, color: T.text.muted, lineHeight: 1.4 }}>{reason}</div>
    </div>
  );
}

// Positive companion to UnavailableCard: the metric IS available, just on a
// different page / from a different source. Keeps the page honest both ways.
function InfoCard({ tag = "Available elsewhere", title, body, accent }) {
  const c = accent || T.status.good;
  return (
    <div style={{
      background: T.surface.card,
      border: `1px solid ${T.border.default}`,
      borderLeft: `3px solid ${c}`,
      borderRadius: 12,
      padding: 16,
      display: "flex", flexDirection: "column", gap: 6,
      minHeight: 120,
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: c }}>
        {tag}
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: T.text.primary }}>{title}</div>
      <div style={{ fontSize: 12, color: T.text.secondary, lineHeight: 1.5 }}>{body}</div>
    </div>
  );
}

function ErrorBanner({ message, onRetry }) {
  return (
    <div style={{
      marginBottom: 12, padding: "10px 14px",
      background: T.status.warnBg, border: `1px solid ${T.status.warn}`,
      borderRadius: 8, fontSize: 12, color: T.text.secondary,
      display: "flex", alignItems: "center", gap: 10,
    }}>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.status.warn }}>● Backend error</span>
      <span style={{ flex: 1 }}><strong>Could not fetch live data:</strong> {message}.</span>
      <button type="button" onClick={onRetry} style={{
        background: T.surface.card, border: `1px solid ${T.border.subtle}`,
        borderRadius: 8, padding: "5px 12px",
        fontSize: 12, fontWeight: 700, color: T.text.primary, cursor: "pointer",
      }}>Retry</button>
    </div>
  );
}

function LoadingShell({ children }) {
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))",
      gap: 14, marginBottom: 12,
    }}>
      {children}
    </div>
  );
}

function LoadingTile() {
  return (
    <div style={{
      background: T.surface.card, border: `1px solid ${T.border.subtle}`,
      borderRadius: 12, padding: 18, height: 220,
      display: "flex", alignItems: "center", justifyContent: "center",
      color: T.text.muted, fontSize: 13,
    }}>
      Loading…
    </div>
  );
}

export function FansPage({ range, rangeId, onOpenMetric }) {
  const { data, loading, error, reload } = useFansData(range, rangeId);
  const word = rangeWordFor(rangeId);
  const Word = capRangeWord(rangeId);

  // Loading state — full-page skeleton, no other UI competes during a fetch.
  if (loading) {
    return <PageLoadingShell tiles={4} />;
  }

  // Error state — friendly empty page (no raw HTTP error leaked).
  if (!data) {
    return <EmptyError title="Couldn't load Fans" error={error} onRetry={reload} />;
  }

  const h = data.hero || {};
  const isTillDate = data.range?.isTillDate;
  // Wire each hero tile to open the MetricDetail modal (same one Home uses).
  // Backend serves trend + mix + contributors per metricId — see
  // METRIC_HANDLERS in routes/investor.routes.js.
  const openMetric = (metricId, value, subValue, label) => {
    if (typeof onOpenMetric === "function") {
      onOpenMetric(metricId, { value, subValue, label });
    }
  };
  const hero = [
    {
      metricId: "total-fans",
      label: "Total fans", icon: "F", variant: "muted-orange",
      value: formatNum(h.totalFans?.value),
      subValue: h.totalFans?.joinedInWindow != null
        ? `+${formatNum(h.totalFans.joinedInWindow)} ${word}`
        : "—",
    },
    {
      metricId: "active-fans-window",
      label: `Tried to call · ${word}`, icon: "T", variant: "muted-violet",
      value: formatNum(h.activeWindow?.value),
      subValue: h.activeWindow?.retentionPct != null ? `${h.activeWindow.retentionPct}% of all fans` : "—",
    },
    {
      metricId: "paying-fans-window",
      label: `Paying fans · ${word}`, icon: "P", variant: "muted-lime",
      value: formatNum(h.payingFans?.value),
      subValue: h.payingFans?.conversionPct != null ? `${h.payingFans.conversionPct}% conversion` : "—",
    },
    {
      metricId: "brought-by-fans-window",
      label: `Brought by friends · ${word}`, icon: "R", variant: "muted-magenta",
      value: formatNum(h.broughtByFans?.value),
      subValue: h.broughtByFans?.invitedBy != null ? `by ${formatNum(h.broughtByFans.invitedBy)} inviting fans` : "—",
    },
  ];

  const trendPoints = data.trend?.totalFansLast12Months || [];
  const latestTotal = trendPoints[trendPoints.length - 1]?.value || 0;
  const prevTotal   = trendPoints[trendPoints.length - 2]?.value || 0;
  const trendDelta  = prevTotal > 0 ? ((latestTotal - prevTotal) / prevTotal) * 100 : 0;

  const tiers = (data.engagementTiers || []).filter(t => (t.value || 0) > 0);
  const tiersTotal = tiers.reduce((sum, t) => sum + (t.value || 0), 0);

  const topSpenders = (data.topSpenders || []).map((r) => {
    // Headline (ranked) = REAL CASH the fan paid Fancall = wallet top-ups via a
    // real rail (UPI/card). Plus the credit Fancall actually GRANTED (referral /
    // promo / admin), shown separately. We intentionally DON'T show "call value"
    // as spend: it doesn't reconcile with wallet funding (inflated by internal
    // youtuber-earning entries + re-spent refunds), so it would mislead.
    const realCash = r.realCash != null ? r.realCash : r.totalSpent;
    const credit   = r.freeCredit || 0;
    const calls    = r.paidCalls != null ? r.paidCalls : r.transactionCount;
    const callsTxt = calls > 0 ? `${calls} paid ${calls === 1 ? 'call' : 'calls'}` : 'no paid calls';
    const line2 = credit > 0
      ? `${callsTxt} · + ${formatINR(credit)} Fancall credit (recharge bonus + referral/promo)`
      : callsTxt;
    return {
      name: r.name || r.fanHandle,
      value: realCash,
      displayValue: formatINR(realCash),
      subtitle: line2,
      subtitle2: null,
    };
  });

  return (
    <>
      {error && <ErrorBanner message={error} onRetry={reload} />}

      {/* Hero strip — live */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))",
        gap: 14, marginBottom: 12,
      }}>
        {hero.map(t => (
          <Tile key={t.label} {...t} onClick={() => openMetric(t.metricId, t.value, t.subValue, t.label)} />
        ))}
      </div>

      {/* Trend chart — title + granularity adapt to the date pill */}
      {trendPoints.length > 0 && (() => {
        const gran = data.range?.granularity || 'month';
        const granLabel = ({ hour: 'HOURLY', day: 'DAILY', week: 'WEEKLY', month: 'MONTHLY', year: 'YEARLY' })[gran] || '';
        const granWord  = ({ hour: 'hourly',  day: 'daily',  week: 'weekly',  month: 'monthly'  })[gran] || '';
        const titleSuffix = isTillDate ? 'last 12 months' : word;
        return (
          <div id="fans-trend" style={{ marginBottom: 12, scrollMarginTop: 80 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10, padding: "0 4px" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text.primary }}>Total fans — {titleSuffix}</div>
                <div style={{ fontSize: 11, color: T.text.muted, marginTop: 2 }}>live · cumulative count by {granWord.replace(/ly$/, '')}-end</div>
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.text.muted, padding: "4px 10px", background: T.surface.card, border: "1px solid " + T.border.subtle, borderRadius: 999 }}>{granLabel}</span>
            </div>
            <AreaChart
              data={trendPoints}
              format={formatNum}
              summaryLabel={trendPoints[trendPoints.length - 1]?.label || "Latest"}
              summaryValue={formatNum(latestTotal)}
              summaryDelta={(trendDelta >= 0 ? "+" : "") + trendDelta.toFixed(1) + "% vs prev"}
            />
          </div>
        );
      })()}

      {/* Fan acquisition signals — real data from schema (login_provider + user_referral)
          plus Engagement tiers donut */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))",
        gap: 14, marginBottom: 12, alignItems: "stretch",
      }}>
        {/* Real: referred vs organic from user_referral table */}
        {data.referralAcquisition && data.referralAcquisition.total > 0 && (
          <Donut
            title="Referred vs organic"
            subtitle="Fans brought via referral link vs found platform on their own"
            data={[
              { label: "Referred",        value: data.referralAcquisition.referred },
              { label: "Organic / direct", value: data.referralAcquisition.organic  },
            ]}
            format={formatNum}
            centerLabel="Total fans"
            centerValue={formatNum(data.referralAcquisition.total)}
          />
        )}
        {/* Real: login provider distribution (auth method, not channel attribution) */}
        {data.loginProviders && data.loginProviders.length > 0 && (
          <Donut
            title="Fans by login method"
            subtitle="Auth provider (NOT paid-channel attribution — see caveat)"
            data={data.loginProviders}
            format={formatNum}
            centerLabel={Word}
            centerValue={formatNum(data.loginProviders.reduce((s, r) => s + (r.value || 0), 0))}
          />
        )}
        {tiers.length > 0 && (
          <Donut
            title="Fan engagement tiers"
            subtitle={`Fans who completed calls, by call count · ${word}`}
            data={tiers}
            format={formatNum}
            centerLabel="Called"
            centerValue={formatNum(tiersTotal)}
          />
        )}
      </div>

      {/* Caveat banner for the login-provider donut (so investors don't read it as paid attribution) */}
      {data.loginProviders && data.loginProviders.length > 0 && data.unavailable?.attributionCaveat && (
        <div style={{
          marginBottom: 12, padding: "8px 12px",
          background: T.surface.card, border: `1px dashed ${T.border.default}`,
          borderRadius: 8, fontSize: 11, color: T.text.muted, lineHeight: 1.5,
        }}>
          <strong style={{ color: T.text.secondary }}>Note on "Login method":</strong> {data.unavailable.attributionCaveat}
        </div>
      )}

      {/* Top spenders — live, masked handles */}
      {topSpenders.length > 0 && (
        <div id="fans-top-spenders" style={{ marginBottom: 12, scrollMarginTop: 80 }}>
          <Leaderboard
            title={`Top spenders · ${word}`}
            subtitle="Ranked by REAL CASH paid — wallet top-ups via UPI/card (the money that actually reached Fancall). Fancall credit shown separately = recharge bonus (e.g. recharge ₹2,000 get ₹1,000) + referral/promo · CEO-only view"
            rows={topSpenders}
            rightLabel="Real cash"
          />
        </div>
      )}

      <div id="fans-engagement" style={{ scrollMarginTop: 80 }} />

      {/* Engagement section — every value below is computed live from the
          same `fan_calls` CTE that drives the engagement-tier donut. No
          estimates, no fabrications. */}
      {(() => {
        const tiersArr      = data.engagementTiers || [];
        const t1            = tiersArr[0]?.value || 0;  // calls = 1
        const t25           = tiersArr[1]?.value || 0;  // 2-5
        const t620          = tiersArr[2]?.value || 0;  // 6-20
        const tVip          = tiersArr[3]?.value || 0;  // 21+
        // "Paying fans" = SPENT money (payments). "calledFans" = COMPLETED a call
        // (bookings). Tier ratios are call-based → they hang off calledFans, never
        // the paying (spent-money) count. The two are never conflated.
        const payingFans    = data.hero?.payingFans?.value || 0;
        const calledFans    = data.hero?.calledFans?.value || 0;
        const totalFans     = data.hero?.totalFans?.value  || 0;
        const repeatShare   = calledFans > 0 ? ((t25 + t620 + tVip) / calledFans) * 100 : 0;
        const oneCallShare  = calledFans > 0 ? (t1   / calledFans) * 100 : 0;
        const conversionPct = totalFans  > 0 ? (payingFans / totalFans) * 100 : 0;

        return (
          <>
            <Section title="Engagement" accent={T.brand.base} count={3} summary={`Fans who completed calls · ${word}`}>
              <Tile
                label={`Avg calls / calling fan · ${word}`}
                icon="C"
                variant="muted-orange"
                value={data.avgCallsPerPayingFan > 0 ? data.avgCallsPerPayingFan.toFixed(2) : "—"}
                subValue={`across ${formatNum(calledFans)} fans who called`}
              />
              <Tile
                label="Repeat-caller share"
                icon="R"
                variant="muted-lime"
                value={calledFans > 0 ? repeatShare.toFixed(1) + "%" : "—"}
                subValue={`${formatNum(t25 + t620 + tVip)} fans with 2+ calls`}
              />
              <Tile
                label="One-call drop-off"
                icon="X"
                variant="muted-violet"
                value={calledFans > 0 ? oneCallShare.toFixed(1) + "%" : "—"}
                subValue={`${formatNum(t1)} fans called once and never returned`}
              />
            </Section>

            <Section title="Conversion" accent="#7C4DFF" count={1} summary="Funnel from total fans → fans who spent money">
              <Tile
                label="Total → paying conversion"
                icon="P"
                variant="muted-lime"
                value={totalFans > 0 ? conversionPct.toFixed(2) + "%" : "—"}
                subValue={`${formatNum(payingFans)} of ${formatNum(totalFans)} fans spent money on calls`}
              />
            </Section>

            {/* Surface the tier-undercount caveat right under the engagement section
                so the numbers above aren't misread as the absolute truth. */}
            {data.unavailable?.tierUndercount && (
              <div style={{
                marginTop: -4, marginBottom: 12, padding: "8px 12px",
                background: T.surface.card, border: `1px dashed ${T.border.default}`,
                borderRadius: 8, fontSize: 11, color: T.text.muted, lineHeight: 1.5,
              }}>
                <strong style={{ color: T.text.secondary }}>Lower-bound caveat:</strong> {data.unavailable.tierUndercount}
              </div>
            )}
          </>
        );
      })()}

      {/* Acquisition section — entire section is acquisition-channel data; show one unavailable card */}
      <Section title="Acquisition" accent="#7C4DFF" count={2} summary="Where your fans come from — what we can and can't measure today">
        <InfoCard
          tag="Available · Acquisition page"
          title="Paid-acquisition cost (CPA)"
          body={<>Live from <strong>Meta Ads</strong> on the <strong>Acquisition page</strong>: ad spend, app installs, cost-per-install and cost-per-signup — your real paid CPA today. (Open the Acquisition page from the sidebar.)</>}
        />
        <UnavailableCard
          title="Per-fan acquisition source"
          reason={<>Linking each <em>individual</em> fan to the exact ad/channel they came from isn't captured by the app yet — it needs Branch attribution written onto the user at signup (an app change; past fans can't be back-filled). The full cross-channel install breakdown also needs an active Branch <em>Data Feeds</em> subscription.</>}
        />
      </Section>

      <PageGuide groups={FANS_GUIDE} />
    </>
  );
}

// ── Definitions for every card on the Fans page (kept in sync with the queries
//    in dashboard.routes.js fansHandler + investorMetric.queries.js). ──────────
const FANS_GUIDE = [
  {
    section: "Overview cards",
    rows: [
      { card: "Total fans",
        means: "Everyone who finished signing up (phone-verified), counted from launch up to the end of the selected dates.",
        rule: "users where role = fan AND phone_verified = true AND created ≤ range end. Test/synthetic accounts excluded." },
      { card: "Tried to call",
        means: "Fans who started at least one call this period — any outcome, whether it connected or not.",
        rule: "Distinct fans with a call row in celebrity_slot_booking (status ≠ 'available') created in the window." },
      { card: "Paying fans",
        means: "Fans who actually spent money on calls this period (the true 'paying customer' count).",
        rule: "Distinct fans with a paid call/room payment in transaction_history (is_paid=success, not refund, not a wallet top-up) in the window." },
      { card: "Brought by friends",
        means: "New fans this period who joined because another fan referred them. The sub-line shows how many existing fans did the inviting.",
        rule: "user_referral where inviter & joiner are both fans (role 2) and the joiner signed up in the window. The synthetic campaign account (role -7) is excluded." },
    ],
  },
  {
    section: "Growth chart",
    rows: [
      { card: "Total fans (trend)",
        means: "The running total of completed fans, plotted day by day across the period.",
        rule: "Cumulative count of phone-verified fans as of the end of each day in the window." },
    ],
  },
  {
    section: "Acquisition mix",
    rows: [
      { card: "Referred vs organic",
        means: "Of this period's completed fans, how many arrived through a referral link vs found the app on their own.",
        rule: "Window completed fans. 'Referred' = has any user_referral row (friend OR creator). 'Organic' = the rest. (Note: 'Brought by friends' above is the friend-only subset.)" },
      { card: "Fans by login method",
        means: "Which sign-in button completed fans used. This is the login method only — NOT marketing/ad attribution.",
        rule: "Completed fans this period grouped by login_provider (google / phone-OTP / facebook / apple)." },
      { card: "Fan engagement tiers (Called)",
        means: "Of fans who completed at least one call this period, how many calls each made: 1, 2–5, 6–20, 21+.",
        rule: "celebrity_slot_booking status = 'completed' per fan in the window, bucketed by count. Center number = fans who completed ≥1 call." },
    ],
  },
  {
    section: "Engagement",
    rows: [
      { card: "Avg calls / calling fan",
        means: "Average number of completed calls per fan who called this period.",
        rule: "avg(completed calls) across fans with ≥1 completed call (same booking-table base as the tiers above)." },
      { card: "Repeat-caller share",
        means: "Share of calling fans who completed 2 or more calls — your stickiness signal.",
        rule: "(fans with 2+ completed calls) ÷ (fans who completed ≥1 call)." },
      { card: "One-call drop-off",
        means: "Share of calling fans who completed exactly one call and didn't come back.",
        rule: "(fans with exactly 1 completed call) ÷ (fans who completed ≥1 call)." },
    ],
  },
  {
    section: "Conversion",
    rows: [
      { card: "Total → paying conversion",
        means: "Of all completed fans, the share who spent money on calls this period.",
        rule: "Paying fans (spent money) ÷ total fans. Both numbers are the same ones defined above, so they always agree." },
    ],
  },
  {
    section: "Top spenders",
    rows: [
      { card: "Top spenders",
        means: "The 10 fans who paid the most REAL money this period. Real cash = wallet top-ups via UPI/card — money that actually reached Fancall. Fancall credit (shown separately) = the recharge bonus (e.g. recharge ₹2,000 → get ₹1,000 extra) PLUS referral/promo/admin grants. Example that reconciles: arti paid ₹12,000 cash, got ₹6,000 recharge bonus + ₹50 referral = ₹18,050 funding ≈ ₹17,998 of calls.",
        rule: "Ranked by real_cash = SUM(total_amount) where is_add_credit=true AND payment_method is a real rail. free_credit = SUM(pre_calculated_wallet_balance_amount) [recharge bonus] + SUM(total_amount where payment_method is NOT a real rail) [referral/promo/admin], on is_add_credit rows. 'Paid calls' = COUNT of paid call-bookings. NOTE: raw 'call value' (sum of call-booking amounts) is not shown as spend — for a few fans it still exceeds funding due to internal youtuber-earning entries; under investigation." },
    ],
  },
  {
    section: "Acquisition cost",
    rows: [
      { card: "Paid CPA (Meta Ads)",
        means: "What you pay in ads to acquire fans — ad spend, app installs, cost-per-install and cost-per-signup.",
        rule: "LIVE on the Acquisition page, pulled from the Meta Ads API. Aggregate (the Meta channel), not per individual fan." },
      { card: "Per-fan acquisition source",
        means: "Linking each individual fan to the exact ad/channel that brought them in.",
        rule: "Not available — the app doesn't store a source on each user. Needs Branch attribution written onto the user at signup (an app change; past fans can't be back-filled). The full cross-channel breakdown also needs an active Branch Data Feeds subscription." },
    ],
  },
];
